/**
 * WhatsApp API Service
 * Serviço para integração com a API de WhatsApp
 * Documentação: API_WHATSAPP.md
 */

const WHATSAPP_API_URL = "https://whatsapp.cloud.efrata.inf.br/api";

export interface WhatsAppMessage {
  id?: string | number;
  phone: string;
  text: string;
}

export interface BulkMessageResult {
  id: string | number;
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Formata um número de telefone para o padrão internacional (55DDDNNNNNNNNN)
 * Remove caracteres não numéricos e adiciona código do país se necessário
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  // Remove todos os caracteres não numéricos
  let cleaned = phone.replace(/\D/g, "");

  // Se começar com 0, remove o zero inicial
  if (cleaned.startsWith("0")) {
    cleaned = cleaned.substring(1);
  }

  // Se não tem o código do país (55), adiciona
  if (!cleaned.startsWith("55")) {
    cleaned = "55" + cleaned;
  }

  return cleaned;
};

/**
 * Verifica se um número de telefone é válido para envio
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const formatted = formatPhoneNumber(phone);
  // Número válido: 55 + DDD (2 dígitos) + número (8 ou 9 dígitos)
  return formatted.length >= 12 && formatted.length <= 13;
};

// Nome da instância usada pelo FlowPet
const WHATSAPP_INSTANCE_NAME = "whatsapp-flowpet-madre";

/**
 * Envia uma mensagem de texto via WhatsApp
 */
export const sendWhatsAppMessage = async (
  phone: string,
  text: string
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const formattedPhone = formatPhoneNumber(phone);

    if (!isValidPhoneNumber(phone)) {
      return { success: false, error: "Número de telefone inválido" };
    }

    const response = await fetch(`${WHATSAPP_API_URL}/messages/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        instanceName: WHATSAPP_INSTANCE_NAME,
        phone: formattedPhone,
        text,
      }),
    });

    const data = await response.json();

    console.log("WhatsApp send message response:", data); // Debug log

    if (data.success) {
      return { success: true, messageId: data.messageId || data.data?.key?.id };
    } else {
      return { success: false, error: data.error || "Erro ao enviar mensagem" };
    }
  } catch (error) {
    console.error("Erro ao enviar mensagem WhatsApp:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
};

/**
 * Envia múltiplas mensagens em massa
 */
export const sendBulkWhatsAppMessages = async (
  messages: WhatsAppMessage[]
): Promise<{ success: boolean; results: BulkMessageResult[] }> => {
  try {
    // Formata todos os números de telefone
    const formattedMessages = messages.map((msg) => ({
      ...msg,
      phone: formatPhoneNumber(msg.phone),
    }));

    const response = await fetch(`${WHATSAPP_API_URL}/messages/bulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        instanceName: WHATSAPP_INSTANCE_NAME,
        messages: formattedMessages,
      }),
    });

    const data = await response.json();

    console.log("WhatsApp bulk send response:", data); // Debug log

    return {
      success: data.success ?? false,
      results: data.results || [],
    };
  } catch (error) {
    console.error("Erro ao enviar mensagens em massa:", error);
    return {
      success: false,
      results: messages.map((msg) => ({
        id: msg.id || "",
        success: false,
        error: error instanceof Error ? error.message : "Erro desconhecido",
      })),
    };
  }
};

/**
 * Cria uma nova instância do WhatsApp
 * Se a instância já existir (erro 403), retorna sucesso pois a instância está disponível
 */
export const createWhatsAppInstance = async (
  instanceName: string
): Promise<{
  success: boolean;
  data?: any;
  error?: string;
  alreadyExists?: boolean;
}> => {
  try {
    const response = await fetch(`${WHATSAPP_API_URL}/instance/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ instanceName }),
    });

    const data = await response.json();

    // Se retornou 403 (Forbidden) com mensagem de "already in use", a instância já existe
    // Isso não é um erro, podemos usar a instância existente
    if (response.status === 403 || data.error === "Forbidden") {
      console.log("Instância já existe, usando a existente:", instanceName);
      return {
        success: true,
        alreadyExists: true,
        data: { instanceName },
      };
    }

    return {
      success: data.success ?? true,
      data: data.data || data,
    };
  } catch (error) {
    console.error("Erro ao criar instância WhatsApp:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
};

/**
 * Obtém o QR Code para conectar o WhatsApp
 */
export const getWhatsAppQRCode = async (
  instanceName: string = "default"
): Promise<{ success: boolean; qrcode?: string; error?: string }> => {
  try {
    const response = await fetch(
      `${WHATSAPP_API_URL}/instance/connect/${instanceName}`
    );
    const data = await response.json();

    console.log("WhatsApp QR Code response:", data); // Debug log

    // A API retorna o QR Code em diferentes estruturas dependendo da versão
    // Estrutura 1: data.data.base64 (Evolution API atual)
    // Estrutura 2: data.instance.qrcode (Evolution API alternativa)
    // Estrutura 3: data.qrcode.base64 (documentação antiga)
    // Estrutura 4: data.qrcode (string direta)
    // Estrutura 5: data.base64 (Evolution API direta)
    let qrcodeValue: string | null = null;

    if (data.data?.base64) {
      qrcodeValue = data.data.base64;
    } else if (data.base64) {
      qrcodeValue = data.base64;
    } else if (data.instance?.qrcode) {
      qrcodeValue = data.instance.qrcode;
    } else if (data.qrcode?.base64) {
      qrcodeValue = data.qrcode.base64;
    } else if (typeof data.qrcode === "string") {
      qrcodeValue = data.qrcode;
    }

    if (qrcodeValue) {
      return {
        success: true,
        qrcode: qrcodeValue,
      };
    }

    // Se já está conectado, retorna sucesso sem QR Code
    if (
      data.state === "open" ||
      data.instance?.state === "open" ||
      data.data?.state === "open"
    ) {
      return { success: true };
    }

    return {
      success: false,
      error: data.error || data.message || "Não foi possível obter QR Code",
    };
  } catch (error) {
    console.error("Erro ao obter QR Code do WhatsApp:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
};

/**
 * Verifica o status da conexão do WhatsApp
 * A API pode retornar o estado em diferentes estruturas:
 * - { state: 'open' } - estrutura simples
 * - { data: { state: 'open' } } - estrutura aninhada
 * - { data: { instance: { state: 'open' } } } - estrutura Evolution API atual
 * - { success: true, data: { state: 'open' } } - estrutura completa
 */
export const getWhatsAppStatus = async (
  instanceName: string = "default"
): Promise<{ connected: boolean; state: string }> => {
  try {
    const response = await fetch(
      `${WHATSAPP_API_URL}/instance/status/${instanceName}`
    );
    const data = await response.json();

    console.log("WhatsApp status response:", data); // Debug log

    // Verifica o estado em diferentes estruturas possíveis
    // A Evolution API retorna: { success: true, data: { instance: { instanceName, state } } }
    const state =
      data.data?.instance?.state ||
      data.data?.state ||
      data.instance?.state ||
      data.state ||
      "unknown";

    return {
      connected: state === "open",
      state: state,
    };
  } catch (error) {
    console.error("Erro ao verificar status do WhatsApp:", error);
    return { connected: false, state: "error" };
  }
};

/**
 * Lista todas as instâncias do WhatsApp
 */
export const listWhatsAppInstances = async (): Promise<{
  success: boolean;
  instances: any[];
  error?: string;
}> => {
  try {
    const response = await fetch(`${WHATSAPP_API_URL}/instance/list`);
    const data = await response.json();

    return {
      success: true,
      instances: data.instances || [],
    };
  } catch (error) {
    console.error("Erro ao listar instâncias WhatsApp:", error);
    return {
      success: false,
      instances: [],
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
};

/**
 * Desconecta (logout) de uma instância do WhatsApp
 */
export const logoutWhatsAppInstance = async (
  instanceName: string = "default"
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      `${WHATSAPP_API_URL}/instance/logout/${instanceName}`,
      {
        method: "POST",
      }
    );
    const data = await response.json();

    return { success: data.success ?? true };
  } catch (error) {
    console.error("Erro ao desconectar WhatsApp:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
};

/**
 * Gera uma mensagem template para notificação
 */
export const generateNotificationTemplate = (
  tutorName: string,
  petName: string,
  notificationTitle: string,
  notificationSummary: string
): string => {
  return `Olá ${tutorName}!

Lembrete do PetShop Da Madre: ${notificationTitle}

Pet: ${petName}
${notificationSummary}

Em caso de dúvidas, entre em contato!`;
};

/**
 * Obtém o melhor telefone disponível do tutor
 */
export const getBestPhoneNumber = (
  owner:
    | { phone?: string; secondaryPhone?: string; mobile?: string }
    | null
    | undefined
): string | null => {
  if (!owner) return null;

  // Prioridade: mobile > phone > secondaryPhone
  if (owner.mobile && isValidPhoneNumber(owner.mobile)) {
    return owner.mobile;
  }
  if (owner.phone && isValidPhoneNumber(owner.phone)) {
    return owner.phone;
  }
  if (owner.secondaryPhone && isValidPhoneNumber(owner.secondaryPhone)) {
    return owner.secondaryPhone;
  }

  return null;
};

export default {
  formatPhoneNumber,
  isValidPhoneNumber,
  sendWhatsAppMessage,
  sendBulkWhatsAppMessages,
  createWhatsAppInstance,
  getWhatsAppQRCode,
  getWhatsAppStatus,
  listWhatsAppInstances,
  logoutWhatsAppInstance,
  generateNotificationTemplate,
  getBestPhoneNumber,
};
